import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { en } from './en.js';
import { fr } from './fr.js';
import { uk } from './uk.js';

export const LOCALES = ['fr', 'en', 'uk'] as const;
export type Locale = (typeof LOCALES)[number];

/** Clés de traduction : celles du dictionnaire français font foi ; les autres doivent les couvrir. */
export type MessageKey = keyof typeof fr;

const DICTS: Record<Locale, Record<MessageKey, string>> = { fr, en, uk };

const STORAGE_KEY = 'ddp.locale';

/** Langue initiale : choix mémorisé, sinon la langue du navigateur, sinon français. */
function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (LOCALES as readonly string[]).includes(saved)) return saved as Locale;
  } catch {
    // localStorage indisponible (mode privé strict) : on retombe sur la détection.
  }
  const navLang =
    typeof navigator !== 'undefined' ? navigator.language.toLowerCase().slice(0, 2) : '';
  return (LOCALES as readonly string[]).includes(navLang) ? (navLang as Locale) : 'fr';
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Traduit une clé ; à défaut, renvoie la clé (visible en développement, jamais du vide). */
  t: (key: MessageKey) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persistance best-effort : le choix reste actif pour la session même sans stockage.
    }
    if (typeof document !== 'undefined') document.documentElement.lang = next;
  }, []);

  // Aligne l'attribut lang du document sur la langue courante, y compris au premier rendu :
  // sans cela l'accessibilité (lecteurs d'écran, césure) verrait toujours la valeur du HTML statique.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback((key: MessageKey) => DICTS[locale][key] ?? key, [locale]);

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n doit être utilisé sous <I18nProvider>');
  return ctx;
}

/** Raccourci le plus courant : `const t = useT();` puis `t('overview.title')`. */
export function useT(): I18nContextValue['t'] {
  return useI18n().t;
}
